/**
 * Luxe-ledenpoort, frontend: is deze deal een Luxe-deal?
 *
 * Luxe-deals (CMS-categorie exact `luxe`) zijn alleen voor ingelogde klanten
 * (Dennis 28-09-2026, "ingelogd = lid"). De echte poort zit in de backend
 * (site-backend #3808 `isLuxeDeal`/`assertLuxeAccess`, #3811 detailroutes,
 * #3812/#3821 GET /deals/luxe): een anonieme bezoeker krijgt daar een 404.
 * Deze module is de frontend-spiegel van dezelfde herkenning, voor twee
 * plekken die de backend niet kan afdekken:
 *
 *   1. `src/lib/structured-data.js`: nooit een Product/Offer-JSON-LD voor een
 *      Luxe-deal, ook niet als een ingelogd lid de pagina bekijkt (JSON-LD is
 *      voor crawlers, en een gerenderde ledenpagina mag nooit prijsdata voor
 *      zoekmachines uitzenden).
 *   2. `/luxe/[dealId]`: die route toont ALLEEN een deal die hier als Luxe
 *      herkend wordt. De backend-detaildeur geeft voor een gewone deal gewoon
 *      de gewone deal terug; zonder deze check zou /luxe/<gewone id> een
 *      gewone deal in de ledenomgeving tonen.
 *
 * Herkenning, gelijk aan de backend (`src/deals/luxe-access.util.ts`):
 *   - `category_ids`/`categoryIds` draagt het exacte token `luxe`, of
 *   - de primaire categorie `category_id`/`categoryId` is `luxe`.
 * Per token: JSON-decoderen tot een vast punt, Unicode-NFKC, onzichtbare
 * opmaaktekens (\p{Cf}) weg, trimmen, kleine letters. `luxe-wellness`,
 * `luxehotels` en `Luxe weekend` zijn GEEN Luxe; `theme`/`destination` tellen
 * niet mee (vrije velden van gewone deals).
 *
 * Kapotte data die het token toch draagt (`'["luxe"'`, `'[luxe]'`) telt als
 * Luxe (woordgrens-match, alleen op strings die niet als nette JSON te lezen
 * zijn), en een uitzondering tijdens het lezen ook. Dat is dezelfde
 * dichtvalrichting als de backend.
 *
 * Eén bewust verschil met de backend: een deal-object ZONDER categorievelden
 * is hier NIET Luxe (de backend behandelt dat als Luxe). De frontend krijgt
 * veel verschillende rij-vormen (lijstkaarten, voucherrijen, feedrijen) waar
 * die velden legitiem ontbreken; fail-closed zou daar de JSON-LD van gewone
 * deals weghalen. Voor de /luxe-route is de richting toch dicht: daar moet een
 * deal positief als Luxe herkend worden, anders volgt notFound().
 */

/** Het marker-token in `cms_deals.category_ids` (en de categorienaam). */
export const LUXE_CATEGORY_MARKER = "luxe";

const LUXE_LOOSE_TOKEN = /(^|[^\p{L}\p{N}_-])luxe($|[^\p{L}\p{N}_-])/u;
const FORMAT_CHARS = /\p{Cf}/gu;

function normalizeToken(token) {
  return String(token)
    .normalize("NFKC")
    .replace(FORMAT_CHARS, "")
    .trim()
    .toLowerCase();
}

/**
 * Zelfde parser als backend `parseCategoryIds`: array, (meervoudig)
 * JSON-gecodeerde string, of een kaal token. Alles anders = geen tokens.
 */
function parseCategoryIds(value) {
  if (Array.isArray(value)) return value.map((el) => String(el).trim());
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (trimmed === "") return [];
  if (trimmed.startsWith("[") || trimmed.startsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map((el) => String(el).trim());
      if (typeof parsed === "string") return parseCategoryIds(parsed);
    } catch {
      // Kapotte JSON: valt terug op het kale token hieronder; de losse
      // woordgrens-match in hasLuxeMarker beslist dan.
    }
  }
  return [trimmed];
}

/** True als de string na het afpellen van JSON-lagen een nette array/token is. */
function isCleanlyParseable(value) {
  let current = value;
  for (;;) {
    const trimmed = current.trim();
    if (!trimmed.startsWith("[") && !trimmed.startsWith('"')) {
      return !/[[\]",]/.test(trimmed);
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.every((el) => typeof el === "string");
      if (typeof parsed !== "string") return false;
      current = parsed;
    } catch {
      return false;
    }
  }
}

function hasLuxeMarker(categoryIds) {
  if (categoryIds === null || categoryIds === undefined) return false;
  try {
    if (parseCategoryIds(categoryIds).some((t) => normalizeToken(t) === LUXE_CATEGORY_MARKER)) {
      return true;
    }
    if (typeof categoryIds === "string") {
      if (isCleanlyParseable(categoryIds)) return false;
      return LUXE_LOOSE_TOKEN.test(normalizeToken(categoryIds));
    }
    const exotic = Array.isArray(categoryIds)
      ? categoryIds.filter((el) => typeof el !== "string")
      : categoryIds;
    if (Array.isArray(exotic) && exotic.length === 0) return false;
    const text = JSON.stringify(exotic);
    if (typeof text !== "string") return false;
    return LUXE_LOOSE_TOKEN.test(normalizeToken(text));
  } catch {
    return true;
  }
}

/**
 * Is deze deal een Luxe-deal? Leest zowel snake_case (`category_ids`,
 * `category_id`) als camelCase (`categoryIds`, `categoryId`), want de BFF
 * levert afhankelijk van de route een van beide of allebei. `null`/geen
 * object = geen deal = false. Zie de bestandskop voor het ene bewuste
 * verschil met de backend (ontbrekende velden).
 */
export function isLuxeDeal(deal) {
  if (!deal || typeof deal !== "object") return false;
  try {
    if (hasLuxeMarker(deal.category_ids) || hasLuxeMarker(deal.categoryIds)) {
      return true;
    }
    for (const primary of [deal.category_id, deal.categoryId]) {
      if (primary === null || primary === undefined) continue;
      if (typeof primary !== "string") {
        // Een getal of object als primaire categorie komt in de CMS-data niet
        // voor; alleen dichtvallen als de vorm het token draagt.
        if (hasLuxeMarker(primary)) return true;
        continue;
      }
      if (hasLuxeMarker(primary)) return true;
    }
    return false;
  } catch {
    return true;
  }
}
